import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, Building2, Compass, Landmark, BriefcaseBusiness, Truck, Check } from "lucide-react";
import { Link } from "wouter";
import { SERVICES, serviceInquiryHref } from "@shared/services";
import { SITE, trackEvent } from "@/lib/site";
import { Button } from "@/components/ui/button";
const icons = [Building2, Compass, BriefcaseBusiness, Landmark, Truck];
export function ServiceOverview() {
  const {
    tx,
    tf,
    href: localeHref,
    dateLocale
  } = useLanguage();
  return <section id="leistungen" className="scroll-mt-28 bg-[#F7F3EB] py-20 lg:py-28">
    <div className="container">
      <div className="grid gap-6 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
        <div><p className="eyebrow">{tx("UNSERE LEISTUNGEN")}</p><h2 className="mt-5 display-md font-semibold">{tx("Ein Ansprechpartner.")}<br />{tx("Spezialisten für jeden Bereich.")}</h2></div>
        <div className="lg:pl-12"><p className="max-w-xl text-sm leading-7 text-[#57534D]">{tx("Fünf Leistungsbereiche, die ineinandergreifen. Sie bestimmen das Ziel – wir koordinieren die passenden Schritte und Ansprechpartner.")}</p><Link href="/leistungen" className="mt-5 inline-flex items-center gap-3 text-xs font-bold uppercase tracking-widest">{tx("Alle Leistungen im Detail ")}<ArrowRight className="h-4 w-4" /></Link></div>
      </div>
      <div className="mt-12 grid gap-5 md:grid-cols-6">
        {tx(SERVICES.map((service, index) => {
          const Icon = icons[index];
          const featured = index === 0;
          return <article key={service.id} className={`group relative flex flex-col overflow-hidden border ${index < 2 ? "md:col-span-3" : "md:col-span-2"} ${featured ? "border-black bg-[#101010] text-white" : "border-black/10 bg-[#FFFDF9] text-[#171717]"}`}>
            {tx(featured && <><img src={SITE.images.stay} alt={tx("Mediterrane Architektur als Illustration des Immobilien-Service")} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25 transition-transform duration-300 group-hover:scale-[1.025]" /><div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/20" /></>)}
            <div className="relative flex h-full flex-col p-7 lg:p-8">
              <div className="flex items-center justify-between"><Icon className="h-6 w-6" strokeWidth={1.25} /><span className={`text-xs tracking-[.2em] ${featured ? "text-white/65" : "text-[#69645C]"}`}>{tx(service.number)}</span></div>
              <p className={`mt-9 text-[10px] font-semibold uppercase tracking-[.14em] ${featured ? "text-[#D8BC88]" : "text-[#76582E]"}`}>{tx(service.claim)}</p>
              <h3 className="mt-3 text-[2rem] font-semibold leading-none lg:text-4xl">{tx(service.name)}</h3>
              <p className={`mt-5 flex-1 text-sm leading-7 ${featured ? "text-white/80" : "text-[#57534D]"}`}>{tx(service.summary)}</p>
              <Link href={service.href} onClick={() => trackEvent("service_clicked", {
                service: service.id,
                placement: "overview"
              })} className={`mt-7 flex items-center justify-between gap-3 border-t pt-5 text-xs font-bold ${featured ? "border-white/25" : "border-black/15"}`}>{tx(service.cta)}<ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-1" /></Link>
            </div>
          </article>;
        }))}
      </div>
    </div>
  </section>;
}
export function ServiceDetails({
  serviceId,
  standalone = false
}: {
  serviceId: string;
  standalone?: boolean;
}) {
  const {
    tx,
    tf,
    href: localeHref,
    dateLocale
  } = useLanguage();
  const service = SERVICES.find(item => item.id === serviceId);
  if (!service) return null;
  return <section id={service.id} className={`scroll-mt-28 ${standalone ? "bg-[#FFFDF9] py-20" : "border-t border-black/10 py-16 lg:py-24"}`}>
    <div className={standalone ? "container" : ""}>
      <div className="grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:gap-20">
        <div><p className="eyebrow">{tx(service.number)}{tx(" / SERVICE")}</p><h2 className="mt-4 display-md font-semibold">{tx(service.name)}</h2><p className="mt-5 text-lg font-medium">{tx(service.claim)}</p><p className="mt-5 max-w-xl text-sm leading-8 text-[#57534D]">{tx(service.description)}</p></div>
        <div>
          <h3 className="font-sans text-xs font-bold uppercase tracking-widest">{tx("Leistungen im Überblick")}</h3>
          <ul className="mt-6 grid gap-x-6 sm:grid-cols-2">{tx(service.features.map(feature => <li key={feature} className="flex gap-3 border-t border-black/10 py-4 text-sm leading-6"><Check className="mt-1 h-4 w-4 shrink-0" />{tx(feature)}</li>))}</ul>
          <p className="mt-5 border-l-2 border-[#A98950] pl-4 text-xs leading-6 text-[#66615A]">{tx(service.note)}</p>
          <Button asChild size="lg" className="mt-7 max-w-full whitespace-normal rounded-none bg-[#171717] px-6 text-white hover:bg-black"><Link href={service.id === "relocation" ? service.href : serviceInquiryHref(service.interest)}>{tx(service.cta)}<ArrowRight className="shrink-0" /></Link></Button>
        </div>
      </div>
    </div>
  </section>;
}
